import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import type {
  UploadedFile,
  UploadDocumentsFormData,
} from '../schemas/upload-documents.schema';
import { uploadDocumentsDefaultValues } from '../schemas/upload-documents.schema';

/**
 * In-memory registry for actual File objects keyed by their upload ID.
 * Not persisted — File objects are not serializable. Cleared on removal.
 */
export const uploadedFileRegistry = new Map<string, File>();

/**
 * Upload Documents store — file upload manager (thin coordinator)
 *
 * This store is a file-upload manager, not a classic text-input form.
 * RHF is a poor fit for pure file-upload state management so validation
 * stays in the page (UploadDocumentsPage.tsx), which derives isValid from
 * the uploadedFiles list and calls setValidity() to update this store.
 *
 * Removed: errors, isDirty, hasInteracted, validateForm, setHasInteracted,
 * clearErrors — all legacy validation plumbing.
 */

interface UploadDocumentsState extends BaseStoreState {
  formData: UploadDocumentsFormData;
  isValid: boolean;
  uploadNetworkError: string | null;
}

export interface UploadDocumentsStore extends UploadDocumentsState {
  setValidity: (_valid: boolean) => void;
  addFiles: (_files: UploadedFile[]) => void;
  updateFile: (_fileId: string, _patch: Partial<UploadedFile>) => void;
  removeFile: (_fileId: string) => void;
  resetForm: () => void;
  setUploadNetworkError: (_err: string | null) => void;
}

const initialFormData: UploadDocumentsFormData = uploadDocumentsDefaultValues;

export const useUploadDocumentsStore =
  createPersistedStore<UploadDocumentsState>(
    'upload-documents',
    {
      formData: initialFormData,
      isValid: false,
      uploadNetworkError: null,
    },
    (set, get) => ({
      setValidity: (valid: boolean) => set({ isValid: valid }),

      addFiles: (files: UploadedFile[]) => {
        const { formData } = get();
        set({
          formData: { uploadedFiles: [...formData.uploadedFiles, ...files] },
        });
      },

      updateFile: (fileId: string, patch: Partial<UploadedFile>) => {
        const { formData } = get();
        const updated = formData.uploadedFiles.map((f: UploadedFile) =>
          f.id === fileId ? { ...f, ...patch } : f
        );
        set({ formData: { uploadedFiles: updated } });
      },

      removeFile: (fileId: string) => {
        const { formData } = get();
        set({
          formData: {
            uploadedFiles: formData.uploadedFiles.filter(
              (f: UploadedFile) => f.id !== fileId
            ),
          },
        });
      },

      resetForm: () =>
        set({
          formData: initialFormData,
          isValid: false,
          loading: false,
          error: null,
          uploadNetworkError: null,
        }),

      setUploadNetworkError: (err: string | null) =>
        set({
          uploadNetworkError: err,
          formData: initialFormData,
          isValid: false,
        }),
    }),
    {
      partialize: (state) => ({
        formData: state.formData,
      }),
      clearOnRefresh: ['formData'],
    }
  ) as unknown as PersistedStoreHook<UploadDocumentsStore>;

// Selectors
export const useUploadDocumentsFormData = () =>
  useUploadDocumentsStore((s) => s.formData);
export const useUploadDocumentsIsValid = () =>
  useUploadDocumentsStore((s) => s.isValid);
export const useUploadDocumentsLoading = () =>
  useUploadDocumentsStore((s) => s.loading);
export const useUploadDocumentsNetworkError = () =>
  useUploadDocumentsStore((s) => s.uploadNetworkError);
